import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Idea } from '@/hooks/useIdeas';
import ShareButtons from './ShareButtons';
import IdeaComments from './IdeaComments';

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

interface IdeaCardProps {
  idea: Idea;
  voted: boolean;
  onVote: (id: string) => void;
  defaultOpen?: boolean;
  linkToPage?: boolean;
}

const IdeaCard = ({ idea, voted, onVote, defaultOpen = false, linkToPage = true }: IdeaCardProps) => {
  const [showComments, setShowComments] = useState(defaultOpen);
  const [commentCount, setCommentCount] = useState(idea.comment_count);

  return (
    <article className="rounded-lg border border-border bg-card/50 p-5 hover:border-primary/40 transition-colors">
      <div className="flex gap-4">
        <button
          onClick={() => onVote(idea.id)}
          disabled={voted}
          aria-label={voted ? 'Already upvoted' : 'Upvote this idea'}
          className={`shrink-0 h-14 w-12 rounded border flex flex-col items-center justify-center transition-colors ${
            voted
              ? 'border-primary bg-primary/20 text-foreground'
              : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'
          }`}
        >
          <span className="text-sm leading-none">▲</span>
          <span className="font-bebas text-lg leading-none mt-0.5">{idea.vote_count}</span>
        </button>

        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs text-muted-foreground">
            <span className="text-primary">{idea.author_name || 'Anonymous'}</span> · {timeAgo(idea.created_at)}
          </p>
          <p className="mt-2 font-playfair text-lg text-foreground/95 whitespace-pre-wrap break-words">
            {idea.body}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowComments(v => !v)}
              className="font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              💬 {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
            </button>
            {linkToPage && (
              <Link to={`/idea/${idea.id}`} className="font-mono text-xs text-primary hover:underline">
                Open
              </Link>
            )}
          </div>
          <ShareButtons ideaId={idea.id} text={idea.body} />
          {showComments && <IdeaComments ideaId={idea.id} onAdded={() => setCommentCount(c => c + 1)} />}
        </div>
      </div>
    </article>
  );
};

export default IdeaCard;
